export function getTopHoldingsConcentration(holdings, count = 5) {
  if (!Array.isArray(holdings)) return 0;
  return holdings.slice(0, count).reduce((sum, holding) => {
    const weight = parseFloat(holding?.weight);
    return Number.isFinite(weight) ? sum + weight : sum;
  }, 0);
}

export function getConcentrationBadge(concentration, lang = 'zh') {
  const labels = {
    high: { zh: '高度集中', en: 'Concentrated' },
    medium: { zh: '中等分散', en: 'Moderate' },
    low: { zh: '高度分散', en: 'Diversified' },
  };

  let key = 'low';
  if (concentration > 60) key = 'high';
  else if (concentration >= 40) key = 'medium';

  return {
    key,
    label: labels[key][lang] || labels[key].zh,
    className: key,
  };
}

export function getTrendIndicator(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return { value: null, arrow: null, className: 'neutral' };
  }

  const numeric = Number(value);
  if (numeric > 0) {
    return {
      value: `+${numeric.toFixed(1)}%`,
      arrow: 'up',
      className: 'positive',
    };
  }

  if (numeric < 0) {
    return {
      value: `${numeric.toFixed(1)}%`,
      arrow: 'down',
      className: 'negative',
    };
  }

  return {
    value: '0.0%',
    arrow: 'flat',
    className: 'neutral',
  };
}

const SECTOR_LABELS = {
  technology: { zh: '科技', en: 'Tech' },
  financials: { zh: '金融', en: 'Financials' },
  consumer: { zh: '消费', en: 'Consumer' },
  healthcare: { zh: '医疗', en: 'Healthcare' },
  energy: { zh: '能源', en: 'Energy' },
  communication: { zh: '通信', en: 'Communication' },
  industrials: { zh: '工业', en: 'Industrials' },
  etf: { zh: 'ETF', en: 'ETF' },
  unknown: { zh: '其他', en: 'Other' },
};

export const CUSIP_SECTORS = {
  '037833100': { name: 'Apple', sector: 'technology' },
  '594918104': { name: 'Microsoft', sector: 'technology' },
  '67066G104': { name: 'NVIDIA', sector: 'technology' },
  '023135106': { name: 'Amazon', sector: 'consumer' },
  '30303M102': { name: 'Meta Platforms', sector: 'communication' },
  '88160R101': { name: 'Tesla', sector: 'consumer' },
  '19260Q107': { name: 'Coinbase', sector: 'financials' },
  '77543R102': { name: 'Roku', sector: 'communication' },
  '770700102': { name: 'Robinhood', sector: 'financials' },
  '852234103': { name: 'Block', sector: 'financials' },
  '025816109': { name: 'American Express', sector: 'financials' },
  '060505104': { name: 'Bank of America', sector: 'financials' },
  '191216100': { name: 'Coca-Cola', sector: 'consumer' },
  '166764100': { name: 'Chevron', sector: 'energy' },
  '742718109': { name: 'Procter & Gamble', sector: 'consumer' },
  '46434G103': { name: 'iShares Core MSCI Emerging Markets ETF', sector: 'etf' },
  '78462F103': { name: 'SPDR S&P 500 ETF Trust', sector: 'etf' },
  '464287465': { name: 'iShares MSCI EAFE ETF', sector: 'etf' },
  '02079K305': { name: 'Alphabet Class A', sector: 'communication' },
  '02079K107': { name: 'Alphabet Class C', sector: 'communication' },
  '46625H100': { name: 'JPMorgan Chase', sector: 'financials' },
  '92826C839': { name: 'Visa', sector: 'financials' },
  '57636Q104': { name: 'Mastercard', sector: 'financials' },
  '478160104': { name: 'Johnson & Johnson', sector: 'healthcare' },
  '717081103': { name: 'Pfizer', sector: 'healthcare' },
  '580135101': { name: 'McDonald’s', sector: 'consumer' },
  '931142103': { name: 'Walmart', sector: 'consumer' },
  '30231G102': { name: 'Exxon Mobil', sector: 'energy' },
  '91324P102': { name: 'UnitedHealth', sector: 'healthcare' },
  '097023105': { name: 'Boeing', sector: 'industrials' },
  '007903107': { name: 'AMD', sector: 'technology' },
  '64110L106': { name: 'Netflix', sector: 'communication' },
};

export const CUSIP_TICKERS = {
  '037833100': 'AAPL',
  '594918104': 'MSFT',
  '67066G104': 'NVDA',
  '023135106': 'AMZN',
  '30303M102': 'META',
  '88160R101': 'TSLA',
  '19260Q107': 'COIN',
  '025816109': 'AXP',
  '060505104': 'BAC',
  '191216100': 'KO',
  '166764100': 'CVX',
  '742718109': 'PG',
  '02079K305': 'GOOGL',
  '02079K107': 'GOOG',
  '46625H100': 'JPM',
  '92826C839': 'V',
  '57636Q104': 'MA',
  '478160104': 'JNJ',
  '717081103': 'PFE',
  '580135101': 'MCD',
  '931142103': 'WMT',
  '30231G102': 'XOM',
  '91324P102': 'UNH',
  '097023105': 'BA',
  '007903107': 'AMD',
  '64110L106': 'NFLX',
};

function normalizeCusip(cusip) {
  return String(cusip || '').trim().toUpperCase();
}

export function getTickerForCusip(cusip) {
  return CUSIP_TICKERS[normalizeCusip(cusip)] || null;
}

export function compareHoldings(latestHoldings, previousHoldings) {
  const previousByCusip = new Map(
    (previousHoldings || []).map((holding) => [normalizeCusip(holding.cusip), holding])
  );

  return (latestHoldings || []).map((holding) => {
    const previous = previousByCusip.get(normalizeCusip(holding.cusip));
    if (!previous) {
      return { ...holding, changeType: 'new', previousShares: null, shareChangePct: null };
    }

    const latestShares = Number(holding.shares);
    const previousShares = Number(previous.shares);
    const shareChangePct = previousShares
      ? ((latestShares - previousShares) / Math.abs(previousShares)) * 100
      : null;

    let changeType = 'flat';
    if (latestShares > previousShares) changeType = 'increased';
    else if (latestShares < previousShares) changeType = 'decreased';

    return {
      ...holding,
      changeType,
      previousShares,
      shareChangePct: shareChangePct == null ? null : Number(shareChangePct.toFixed(1)),
    };
  });
}

export function summarizeHoldingChanges(latestHoldings, previousHoldings) {
  const summary = { new: 0, increased: 0, decreased: 0, sold: 0 };
  const latestCusips = new Set((latestHoldings || []).map((holding) => normalizeCusip(holding.cusip)));

  (latestHoldings || []).forEach((holding) => {
    if (holding.changeType === 'new') summary.new += 1;
    if (holding.changeType === 'increased') summary.increased += 1;
    if (holding.changeType === 'decreased') summary.decreased += 1;
  });

  (previousHoldings || []).forEach((holding) => {
    if (!latestCusips.has(normalizeCusip(holding.cusip))) summary.sold += 1;
  });

  return summary;
}

export function buildSectorDistribution(holdings, lang = 'zh') {
  const totals = new Map();

  (holdings || []).forEach((holding) => {
    const mapped = CUSIP_SECTORS[normalizeCusip(holding.cusip)];
    const key = mapped?.sector || 'unknown';
    const weight = parseFloat(holding.weight);
    if (!Number.isFinite(weight) || weight <= 0) return;
    totals.set(key, (totals.get(key) || 0) + weight);
  });

  return [...totals.entries()]
    .map(([key, weight]) => ({
      key,
      label: SECTOR_LABELS[key]?.[lang] || SECTOR_LABELS[key]?.en || key,
      weight: Number(weight.toFixed(1)),
    }))
    .sort((a, b) => b.weight - a.weight);
}

export function buildValueTrend(filings) {
  const points = (filings || [])
    .filter((filing) => filing?.filingDate && Number.isFinite(Number(filing.totalValue)))
    .map((filing) => ({
      date: filing.filingDate,
      totalValue: Number(filing.totalValue),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  const qoqChangePct = last && prev && prev.totalValue
    ? Number((((last.totalValue - prev.totalValue) / Math.abs(prev.totalValue)) * 100).toFixed(1))
    : null;

  return { points, qoqChangePct };
}
